/**
 * Drawer — THE one drawer (spec Decision 3 + Amendment A). The kept record drawer
 * (components/ui/drawer.tsx, #64 #90 #131 #171) upgraded to the spec: right-docked, non-modal
 * (no scrim), role="dialog" labelled by its title, opens at min(60vw, 1200px), resizable 480px to
 * 90vw by dragging the grip or by keyboard on it (arrows ±32px, Shift ±128px, Home/End to min/max),
 * width remembered per user, 90vw at 768–1023, full screen below 768 with the grip hidden.
 * Head (key, title, status, actions, close) · tabs · scrolling body · pinned footer. Focus moves to
 * the title on open and back to the opener on close; Escape and ✕ close; a form with unsaved
 * changes (`dirty`) asks before the drawer closes or switches record (`requestDrawerLeave`).
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { FeedbackPill } from './feedback';
import { createAskerStack } from './asker-stack';

/** Just enough of `EventTarget` to check ancestry, so this stays testable without a DOM. */
export interface EscapeTarget {
  closest?: (selector: string) => unknown;
}
export interface EscapeCandidate {
  defaultPrevented: boolean;
  target: EscapeTarget | null | undefined;
}

/**
 * True when an Escape keydown, seen at the document, should close the drawer: not when something
 * already handled it, and not when it belongs to an open popover/menu (`[data-open]`) or a modal.
 */
export function shouldCloseOnEscape({ defaultPrevented, target }: EscapeCandidate): boolean {
  if (defaultPrevented) return false;
  if (target && typeof target.closest === 'function' && (target.closest('[data-open]') || target.closest('[aria-modal="true"]'))) return false;
  return true;
}

export interface FocusCandidate {
  focus: () => void;
  isConnected?: boolean;
}

/** Where focus lands on close: the opener if it is still on the page, else the fallback. */
export function focusReturnTarget<T extends FocusCandidate>(opener: T | null | undefined, fallback: T | null | undefined): T | null {
  if (opener && opener.isConnected !== false) return opener;
  return fallback ?? null;
}

export const DRAWER_MIN_PX = 480;
export const DRAWER_MAX_PCT = 90;
export const DRAWER_DEFAULT_PCT = 60;
export const DRAWER_DEFAULT_CAP_PX = 1200;
export const GRIP_STEP_PX = 32;
export const GRIP_BIG_STEP_PX = 128;

/** The width a drawer opens at before the user has chosen one: min(60vw, 1200px), at least 480. */
export function defaultDrawerWidth(viewport: number): number {
  return clampDrawerWidth(Math.min((viewport * DRAWER_DEFAULT_PCT) / 100, DRAWER_DEFAULT_CAP_PX), viewport);
}

/** A width clamped to [480px, 90vw]; a screen narrower than 480 gets its whole width. */
export function clampDrawerWidth(px: number, viewport: number): number {
  const max = Math.max(DRAWER_MIN_PX, Math.round((viewport * DRAWER_MAX_PCT) / 100));
  return Math.min(viewport, Math.max(DRAWER_MIN_PX, Math.min(max, Math.round(px))));
}

/** The width after a key on the grip (the grip is the drawer's left edge: ← widens). */
export function widthAfterKey(key: string, shift: boolean, width: number, viewport: number): number | null {
  const step = shift ? GRIP_BIG_STEP_PX : GRIP_STEP_PX;
  if (key === 'ArrowLeft') return clampDrawerWidth(width + step, viewport);
  if (key === 'ArrowRight') return clampDrawerWidth(width - step, viewport);
  if (key === 'Home') return clampDrawerWidth(DRAWER_MIN_PX, viewport);
  if (key === 'End') return clampDrawerWidth(viewport, viewport);
  return null;
}

const KEY = (userId: string | null | undefined) => `cigs.kit.drawer.width.${userId ?? 'anon'}`;
const LAST = 'cigs.kit.drawer.width.last';

/** The remembered width in px: this user's, else this browser's last, else none. */
export function rememberedWidth(store: Pick<Storage, 'getItem'> | null | undefined, userId: string | null | undefined): number | null {
  try {
    const raw = store?.getItem(userId ? KEY(userId) : LAST) ?? store?.getItem(LAST) ?? null;
    const n = Number(raw);
    return raw !== null && Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function remember(userId: string | null | undefined, px: number) {
  try {
    localStorage.setItem(KEY(userId), String(Math.round(px)));
    localStorage.setItem(LAST, String(Math.round(px)));
  } catch {
    /* storage blocked: the width lasts for this visit */
  }
}

/* ── Unsaved-changes guard: every open drawer holds one asker, in a stack ─── */
const askers = createAskerStack();

/**
 * Ask the open drawer whether it may let go of its record (choosing another row, closing).
 * Resolves true at once when no drawer holds unsaved changes.
 */
export function requestDrawerLeave(): Promise<boolean> {
  return askers.ask();
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Whose width to remember. */
  userId?: string | null;
  /** Record key, in mono above the title. */
  entityKey?: React.ReactNode;
  title: React.ReactNode;
  /** Status pill(s) under the title. */
  state?: React.ReactNode;
  /** Head actions (Raise request, Edit…). */
  actions?: React.ReactNode;
  /** Where "Open as page" goes; omitted when the record has no page. */
  pageHref?: string;
  /** Tabs (or a description) under the head. */
  headerExtra?: React.ReactNode;
  /** The pinned footer: the form's primary action. */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** Unsaved changes: closing or switching asks first. */
  dirty?: boolean;
  /** R16 #87: told when the person chooses Discard changes, so the edits stop counting before the record changes. */
  onDiscard?: () => void;
  /** Render the same record as a full page: in flow, full width, no grip. */
  asPage?: boolean;
  className?: string;
  /** Accepted for compatibility; the kit drawer docks to the top of the window. */
  top?: number;
  'data-testid'?: string;
}

export function Drawer({ open, onClose, userId, entityKey, title, state, actions, pageHref, headerExtra, footer, children, dirty, onDiscard, asPage, className, ...rest }: DrawerProps) {
  const [viewport, setViewport] = React.useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  const [chosen, setChosen] = React.useState<number | null>(() => rememberedWidth(typeof localStorage === 'undefined' ? null : localStorage, userId));
  const [dragging, setDragging] = React.useState(false);
  const [asking, setAsking] = React.useState<((ok: boolean) => void) | null>(null);
  const titleId = React.useId();
  const headingRef = React.useRef<HTMLHeadingElement | null>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = React.useRef(Boolean(dirty));
  dirtyRef.current = Boolean(dirty);

  React.useEffect(() => setChosen(rememberedWidth(typeof localStorage === 'undefined' ? null : localStorage, userId)), [userId]);
  React.useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const ask = React.useCallback<() => Promise<boolean>>(() => {
    if (!dirtyRef.current) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setAsking(() => resolve));
  }, []);

  React.useEffect(() => {
    // the full page asks too: its Discard modal renders on the page, and peek / back go through requestDrawerLeave (#87 round 6)
    if (!open) return;
    askers.push(ask);
    return () => askers.remove(ask);
  }, [open, ask]);

  const close = React.useCallback(() => {
    void ask().then((ok) => ok && onCloseRef.current());
  }, [ask]);

  // Focus in on open, back to the opener on close.
  React.useEffect(() => {
    if (!open || asPage) return;
    const opener = typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null);
    headingRef.current?.focus();
    return () => {
      const main = document.querySelector('main');
      if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      focusReturnTarget(opener && opener !== document.body ? opener : null, main as HTMLElement | null)?.focus();
    };
  }, [open, asPage]);

  React.useEffect(() => {
    if (!open || asPage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || asking) return;
      if (shouldCloseOnEscape({ defaultPrevented: e.defaultPrevented, target: e.target as EscapeTarget | null })) close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, asPage, close, asking]);

  const width = chosen ? clampDrawerWidth(chosen, viewport) : defaultDrawerWidth(viewport);
  // R-15 C (ADR-030, T-074): the context pane docks beside the content instead of covering it — while it is
  // open the shell's column gives up the pane's width (kit.css, html[data-pane]), so the list compresses.
  React.useEffect(() => {
    if (!open || asPage || typeof document === 'undefined') return;
    const root = document.documentElement;
    const mine = String(Math.round(width));
    root.setAttribute('data-pane', mine);
    root.style.setProperty('--k-pane-w', `${mine}px`);
    return () => {
      if (root.getAttribute('data-pane') !== mine) return;
      root.removeAttribute('data-pane');
      root.style.removeProperty('--k-pane-w');
    };
  }, [open, asPage, width]);
  const commit = React.useCallback(
    (px: number) => {
      const next = clampDrawerWidth(px, window.innerWidth);
      setChosen(next);
      remember(userId, next);
    },
    [userId],
  );

  const onGripPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    setDragging(true);
    const move = (ev: PointerEvent) => commit(window.innerWidth - ev.clientX);
    const up = () => {
      setDragging(false);
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
  };

  const onGripKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const next = widthAfterKey(e.key, e.shiftKey, width, viewport);
    if (next === null) return;
    e.preventDefault();
    commit(next);
  };

  if (!open) return null;
  const Heading = asPage ? 'h1' : 'h2';
  const max = clampDrawerWidth(viewport, viewport);
  return (
    <>
      <section
        role={asPage ? undefined : 'dialog'}
        aria-modal={asPage ? undefined : false}
        aria-labelledby={titleId}
        data-kit={asPage ? 'page' : 'drawer'}
        data-presenter={asPage ? 'page' : 'pane'}
        data-page={asPage ? 'true' : undefined}
        data-testid={rest['data-testid'] ?? 'drawer'}
        className={className ? `k-drawer ${className}` : 'k-drawer'}
        style={asPage ? undefined : ({ '--k-drawer-w': `${width}px` } as React.CSSProperties)}
      >
        {asPage ? null : (
          <button
            type="button"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the drawer"
            aria-valuemin={DRAWER_MIN_PX}
            aria-valuemax={max}
            aria-valuenow={width}
            data-testid="drawer-grip"
            data-kit="grip"
            data-dragging={dragging ? '' : undefined}
            className="k-drawer-grip"
            onPointerDown={onGripPointerDown}
            onKeyDown={onGripKeyDown}
          />
        )}
        <header className="k-d-head">
          <div className="k-d-top">
            <div className="k-d-titles">
              {entityKey ? <span className="k-d-key">{entityKey}</span> : null}
              <Heading id={titleId} ref={headingRef} tabIndex={-1}>
                {title}
              </Heading>
            </div>
            {actions ? <div className="k-actions-row">{actions}</div> : null}
            {pageHref && !asPage ? (
              <Link to={pageHref} className="k-btn" data-kit="button" data-variant="arrowhead">
                Open as page
              </Link>
            ) : null}
            {/* R-15 D2 (T-111): the feedback pill on the record presenter, carrying the record's key */}
            <FeedbackPill recordKey={typeof entityKey === 'string' ? entityKey : null} />
            {asPage ? null : (
              <Button icon aria-label="Close" data-testid="drawer-close" onClick={close}>
                <X aria-hidden />
              </Button>
            )}
          </div>
          {state ? <div className="k-d-state">{state}</div> : null}
        </header>
        {headerExtra ? <div className="k-d-tabs">{headerExtra}</div> : <div />}
        <div className="k-d-body">{children}</div>
        {footer ? <footer className="k-d-foot">{footer}</footer> : <div />}
      </section>
      <Modal
        open={Boolean(asking)}
        size="medium"
        eyebrow="Unsaved changes"
        title={<>Discard your changes to {title}?</>}
        onClose={() => {
          asking?.(false);
          setAsking(null);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                asking?.(false);
                setAsking(null);
              }}
            >
              Keep editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDiscard?.();
                asking?.(true);
                setAsking(null);
              }}
            >
              Discard changes
            </Button>
          </>
        }
      >
        <p>Your edits to this record have not been saved. Leaving now discards them.</p>
      </Modal>
    </>
  );
}
